// Renderizador minimalista para o dossiê. O prompt gera estrutura fixa
// (## headers, listas numeradas/bullet, parágrafos). Renderer cobre só esses casos
// e cai pra <p> em qualquer outra linha. Sem dependência externa.

interface Props {
  texto: string;
}

interface Block {
  type: 'h3' | 'p' | 'ol' | 'ul';
  content: string | string[];
}

function parseBlocks(md: string): Block[] {
  const lines = md.split(/\r?\n/);
  const blocks: Block[] = [];
  let buf: string[] = [];
  let listKind: 'ol' | 'ul' | null = null;

  function flushPara() {
    if (buf.length) {
      blocks.push({ type: 'p', content: buf.join(' ').trim() });
      buf = [];
    }
  }
  function flushList() {
    if (listKind && buf.length) {
      blocks.push({ type: listKind, content: [...buf] });
      buf = [];
      listKind = null;
    }
  }

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushList();
      flushPara();
      continue;
    }
    if (line.startsWith('## ')) {
      flushList();
      flushPara();
      blocks.push({ type: 'h3', content: line.slice(3).trim() });
      continue;
    }
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    const bulletMatch = line.match(/^[-*]\s+(.*)$/);
    if (numMatch) {
      if (listKind !== 'ol') flushList();
      flushPara();
      listKind = 'ol';
      buf.push(numMatch[2] ?? '');
      continue;
    }
    if (bulletMatch) {
      if (listKind !== 'ul') flushList();
      flushPara();
      listKind = 'ul';
      buf.push(bulletMatch[1] ?? '');
      continue;
    }
    if (listKind) flushList();
    buf.push(line);
  }
  flushList();
  flushPara();
  return blocks;
}

// Bold inline **texto**.
function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? (
      <strong key={i} className="text-text font-semibold">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

export function DossieMarkdown({ texto }: Props) {
  const blocks = parseBlocks(texto);
  return (
    <article className="flex flex-col gap-3 text-sm text-text leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === 'h3') {
          return (
            <h3
              key={i}
              className="text-[0.78rem] font-semibold uppercase tracking-wide text-accent mt-2"
            >
              {b.content as string}
            </h3>
          );
        }
        if (b.type === 'p') {
          return (
            <p key={i} className="text-text-2">
              {renderInline(b.content as string)}
            </p>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={i} className="list-decimal pl-5 flex flex-col gap-1 text-text-2">
              {(b.content as string[]).map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ol>
          );
        }
        return (
          <ul key={i} className="list-disc pl-5 flex flex-col gap-1 text-text-2">
            {(b.content as string[]).map((item, j) => (
              <li key={j}>{renderInline(item)}</li>
            ))}
          </ul>
        );
      })}
    </article>
  );
}
